import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import type { IPublicSignupSurveyResolve } from '@brushfire/types'
import {
  persistWebSurveyOnboardingSession,
  persistWebSurveySurveyFirst,
} from '@/features/auth/lib/constants'
import { getErrorMessage } from '@/api/http/getErrorMessage'
import { AuthFooter } from '@/features/auth/components/AuthFooter'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useStartSurveyFirstSession } from '../api/instrument-onboarding-mutations'
import {
  compactRewardLabel,
  formatWelcomeDuration,
  welcomeSubtitle,
} from '../lib/signup-survey-welcome-copy'
import {
  PRIVACY_BODY_PARAGRAPHS,
  PRIVACY_LAST_UPDATED,
  TERMS_BODY_PARAGRAPHS,
  TERMS_LAST_UPDATED,
} from '../lib/policy-content'
import { SignupSurveyWelcomeHowItWorks } from './SignupSurveyWelcomeHowItWorks'
import { SignupSurveyWelcomeTrust } from './SignupSurveyWelcomeTrust'
import { WebSurveyLegalModal } from './WebSurveyLegalModal'
import { RiTeamFill } from '@/shared/icons/remix'
import iconPrivacyCheck from '../assets/welcome/icon-privacy-check.svg'
import {
  welcomeAboutIconWrapSx,
  welcomeAboutSx,
  welcomeAboutTextSx,
  welcomeFooterLinkSx,
  welcomeFooterSepSx,
  welcomeFooterSx,
  welcomeHeadlineSx,
  welcomeHeroGlowBlueSx,
  welcomeHeroGlowYellowSx,
  welcomeInnerSx,
  welcomeKickerSx,
  welcomeLogoSx,
  welcomePageSx,
  welcomeScrollSx,
  welcomePrivacyIconWrapSx,
  welcomePrivacySx,
  welcomeRewardLineSx,
  welcomeRewardSx,
  welcomeStartSx,
  welcomeSubtitleSx,
} from '../styles/signupSurveyWelcomeStyles'

const SUPPORT_MAILTO = 'mailto:support@brushfireresearch.com'

interface SignupSurveyWelcomeViewProps {
  readonly survey: IPublicSignupSurveyResolve
  readonly onStarted: (instrumentId: string) => void
}

export function SignupSurveyWelcomeView({ survey, onStarted }: SignupSurveyWelcomeViewProps) {
  const startSession = useStartSurveyFirstSession()
  const [error, setError] = useState<string | null>(null)
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null)
  const rewardLabel = compactRewardLabel(survey.reward)
  const minutesLabel = formatWelcomeDuration(survey.estimatedCompletionMinutes)

  const handleStart = async () => {
    const token = survey.entryToken?.trim() ?? ''
    if (!token) return
    setError(null)
    try {
      const data = await startSession.mutateAsync(token)
      const sessionId =
        data?.pendingSessionId || data?.onboardingSessionId || data?.guestSessionId
      if (sessionId) persistWebSurveyOnboardingSession(sessionId)
      persistWebSurveySurveyFirst(data?.instrumentId || survey.instrumentId)
      onStarted(survey.instrumentId)
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  return (
    <Box data-testid="signup-survey-welcome" sx={welcomePageSx}>
      <Box sx={welcomeHeroGlowBlueSx} aria-hidden />
      <Box sx={welcomeHeroGlowYellowSx} aria-hidden />
      <Box sx={welcomeScrollSx}>
      <Box sx={welcomeInnerSx}>
        <Box component="img" src={brushfireLogo} alt="Brushfire" sx={welcomeLogoSx} />
        <Typography component="p" sx={welcomeKickerSx}>
          You&apos;re invited
        </Typography>
        <Typography component="h1" sx={welcomeHeadlineSx}>
          Complete a Short Survey.
          {rewardLabel ? (
            <Box component="span" sx={welcomeRewardLineSx} data-testid="signup-survey-welcome-reward">
              Get <Box component="span" sx={welcomeRewardSx}>{rewardLabel}.</Box>
            </Box>
          ) : null}
        </Typography>
        <Typography component="p" sx={welcomeSubtitleSx} data-testid="signup-survey-welcome-subtitle">
          {welcomeSubtitle(minutesLabel, rewardLabel)}
        </Typography>
        {error ? (
          <Alert severity="error" role="alert" sx={{ mt: 2 }} data-testid="signup-survey-welcome-error">
            {error}
          </Alert>
        ) : null}
        <Button
          variant="contained"
          disableElevation
          disabled={startSession.isPending}
          onClick={() => {
            void handleStart()
          }}
          data-testid="signup-survey-welcome-start"
          sx={welcomeStartSx}
        >
          {startSession.isPending ? <CircularProgress size={20} color="inherit" /> : 'Start Survey'}
        </Button>

        <Box sx={welcomeAboutSx} data-testid="signup-survey-welcome-about">
          <Box sx={welcomeAboutIconWrapSx} aria-hidden>
            <RiTeamFill />
          </Box>
          <Typography component="p" sx={welcomeAboutTextSx}>
            <Box component="strong" sx={{ fontWeight: 700, color: '#0F1B33' }}>
              About Brushfire Research:
            </Box>{' '}
            Brushfire Research is a Pennsylvania-based research company that builds panels of real
            people to better understand opinions on local and national topics. We conduct short
            surveys and other research to help capture what people actually think about the issues
            that affect them.
          </Typography>
        </Box>

        <SignupSurveyWelcomeTrust />
        <SignupSurveyWelcomeHowItWorks />

        <Box sx={welcomePrivacySx}>
          <Box sx={welcomePrivacyIconWrapSx}>
            <Box component="img" src={iconPrivacyCheck} alt="" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#0F1B33', mb: 0.75, fontSize: 16, lineHeight: '24px' }}>
              Your privacy matters
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: '18.6px' }}>
              Your responses are confidential and used only for research purposes. We will never
              sell your personal information.
            </Typography>
          </Box>
        </Box>

        <Box sx={welcomeFooterSx}>
          <Link
            component="button"
            type="button"
            onClick={() => setLegalModal('privacy')}
            data-testid="signup-survey-welcome-privacy"
            sx={welcomeFooterLinkSx}
          >
            Privacy Policy
          </Link>
          <Box component="span" sx={welcomeFooterSepSx}>
            ·
          </Box>
          <Link
            component="button"
            type="button"
            onClick={() => setLegalModal('terms')}
            data-testid="signup-survey-welcome-terms"
            sx={welcomeFooterLinkSx}
          >
            Terms of Use
          </Link>
          <Box component="span" sx={welcomeFooterSepSx}>
            ·
          </Box>
          <Link href={SUPPORT_MAILTO} sx={welcomeFooterLinkSx}>
            Contact Support
          </Link>
        </Box>
      </Box>
      </Box>
      <AuthFooter />
      <WebSurveyLegalModal
        open={legalModal === 'terms'}
        title="Terms of Use"
        paragraphs={TERMS_BODY_PARAGRAPHS}
        lastUpdated={TERMS_LAST_UPDATED}
        onClose={() => setLegalModal(null)}
      />
      <WebSurveyLegalModal
        open={legalModal === 'privacy'}
        title="Privacy Policy"
        paragraphs={PRIVACY_BODY_PARAGRAPHS}
        lastUpdated={PRIVACY_LAST_UPDATED}
        onClose={() => setLegalModal(null)}
      />
    </Box>
  )
}
